import {
  getFormProps,
  getInputProps,
  type SubmissionResult,
  useForm,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type Car } from '@prisma/client'
import { Form } from '@remix-run/react'
import { PaymentElement } from '@stripe/react-stripe-js'
import { useState } from 'react'
import { z } from 'zod'
import { paymentErrorMessage } from '#app/features/payment/paymentErrorMessage.ts'
import { calculateDiscountedPrice } from '#app/shared/lib/price.ts'
import {
  emailSchema,
  firstNameSchema,
  phoneSchema,
  postalCodeSchema,
} from '#app/shared/lib/user-validation.js'
import { Spacer } from '#app/shared/ui/data-display/Spacer.js'
import { ErrorList, Field, PhoneField } from '#app/shared/ui/fields.js'
import { Button } from '#app/shared/ui/inputs/button/Button.js'
import { Checkbox } from '#app/shared/ui/inputs/Checkbox.js'
import { CheckboxTermsAndPrivacy } from '#app/shared/ui/inputs/CheckboxTermsAndPrivacy.js'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '#app/shared/ui/navigation/Tabs.js'
import {
  type PaymentType,
  PaymentErrorEnum,
  PaymentEnum,
  type TypeOfPaymentType,
  type WarrantyTypes,
  type WarrantyDurationTypes,
  WarrantyTypeEnum,
  WarrantyDurationEnum,
} from '../../../../../types/enums'
import { useElementsContext } from './stripe/elementProvider'

export const createReservationFormSchema = (maxInitialEntry: number) =>
  z.object({
    email: emailSchema,
    phoneNumber: phoneSchema,
    fullPhone: z.string(),
    name: firstNameSchema,
    postalCode: postalCodeSchema,
    activeNumber: z.number(),
    paymentType: z.enum(['Financiado', 'Al contado']),
    initialEntry: z
      .number()
      .min(0, { message: 'La entrada inicial debe ser mayor o igual a 0' })
      .max(maxInitialEntry, {
        message: `La entrada inicial no puede exceder ${new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(maxInitialEntry)} (50% del valor del vehículo)`,
      }),
    warrantyChoice: z.preprocess(
      (val) => {
        if (typeof val === 'string') {
          try {
            return JSON.parse(val)
          } catch (e) {
            return e
          }
        }
        return val
      },
      z.object({
        type: z.nativeEnum(WarrantyTypeEnum),
        duration: z.nativeEnum(WarrantyDurationEnum),
      }),
    ),
    carAsPayment: z.preprocess((val) => {
      if (typeof val === 'string') {
        return val === 'true'
      }
      return val
    }, z.boolean().optional()),
    hasPackOptima: z.preprocess((val) => {
      if (typeof val === 'string') {
        return val === 'true'
      }
      return val
    }, z.boolean().optional()),
  })

export const ReservationFormSchema = createReservationFormSchema(Infinity)

type Props = {
  paymentMethod: PaymentType
  setPaymentMethod: (pay: PaymentType) => void
  warrantyChoice: {
    type: WarrantyTypes
    duration: WarrantyDurationTypes
  }
  activeNumber: number
  typeOfPayment: TypeOfPaymentType
  initialEntry: number
  initialError: string | null
  submission: any
  car: Car
  hasPackOptima: boolean
}

export default function ReserveForm({
  paymentMethod,
  setPaymentMethod,
  warrantyChoice,
  activeNumber,
  typeOfPayment,
  initialEntry,
  initialError,
  submission,
  car,
  hasPackOptima,
}: Props) {
  console.log('hasPackOptima', hasPackOptima)
  const {
    isElementsReady,
    elements,
    message,
    isLoading,
    stripe,
    handleStripeFromSubmit,
  } = useElementsContext()
  const [isTermsAccepted, setIsTermsAccepted] = useState(false)
  const [carAsPayment, setCarAsPayment] = useState(false)

  const [error, setError] = useState(initialError)
  const [result, setResult] = useState<SubmissionResult<string[]> | undefined>(
    submission?.result,
  )

  // Calculate the maximum initial entry (50% of the vehicle's financing value)
  const financedPrice =
    calculateDiscountedPrice(car.minFinancedAmount ?? 0, car.discount) ?? 0
  const maxInitialEntry = Math.floor(financedPrice * 0.5)

  // Create dynamic schema with the calculated max
  const DynamicReservationFormSchema =
    createReservationFormSchema(maxInitialEntry)

  const [form, fields] = useForm({
    id: 'payment-form',
    lastResult: result,
    constraint: getZodConstraint(DynamicReservationFormSchema),
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: DynamicReservationFormSchema })
    },
    shouldRevalidate: 'onBlur',
  })

  const handleSubmit = async (event: any) => {
    event.preventDefault()
    // verify form
    const fomData = new FormData(event.target)
    const submission = parseWithZod(fomData, {
      schema: DynamicReservationFormSchema,
    })
    if (submission.status !== 'success') {
      return setResult(submission.reply())
    }

    // verify if car is available
    const response = await fetch(`/api/car/availability/verify/${car.id}`)
    const body = (await response.json()) as any
    if (!('isAvailable' in body)) {
      setError(PaymentErrorEnum.GENERIC)
      return
    }

    if (!body.isAvailable) {
      setError(PaymentErrorEnum.NOT_AVAILABLE)
      return
    }

    // cancel payment intent if paypal
    if (paymentMethod === PaymentEnum.PAYPAL) {
      return event.target.submit()
    }

    // if nor paypal, is stripe, verify if stripe is ready, if it is submit it
    if (handleStripeFromSubmit) {
      return handleStripeFromSubmit(fomData)
    }

    // this should never happen, if is paypal, should submit, if stripe couldn't arrive click the button until it is ready
    return
  }
  return (
    <Form
      method="post"
      className={`
        pt-[23px]

        xl:full xl:pl-[100px] xl:pt-[31px]
      `}
      {...getFormProps(form)}
      onSubmit={handleSubmit}
    >
      <p
        className={`
          text-h5-bold text-neutral-900

          lg:text-h3
        `}
      >
        Confirma tu reserva por 600€
      </p>

      <Spacer size="16px" />
      <p className="text-base-medium text-neutral-500">
        Reserva el vehículo durante 5 días laborables. Este monto se te
        descontará del precio final. Un agente se pondrá en contacto contigo
        para establecer la fecha de entrega y el resto del pago.
      </p>
      <Spacer size="28px" sizeMd="36px" />
      <input
        type="hidden"
        name="warrantyChoice"
        value={JSON.stringify(warrantyChoice)}
      />
      <input type="hidden" name="activeNumber" value={activeNumber} />
      <input type="hidden" name="paymentType" value={typeOfPayment} />
      <input type="hidden" name="initialEntry" value={initialEntry} />
      <input
        type="hidden"
        name="carAsPayment"
        value={carAsPayment.toString()}
      />
      <input
        type="hidden"
        name="hasPackOptima"
        value={hasPackOptima.toString()}
      />
      <div className="flex flex-col gap-7">
        <Field
          labelProps={{
            children: 'Nombre',
            className: 'text-subtitle1-bold text-neutral-900',
          }}
          inputProps={{
            ...getInputProps(fields.name, { type: 'text' }),
            autoComplete: 'name',
            placeholder: 'Escribe aquí tu nombre',
            variant: 'outlined',
            className:
              ' max-w-[684px] !mt-3 !h-14 text-base-medium text-neutral-500 border-neutral-100',
            size: '64px',
          }}
          errors={fields.name.errors}
        />

        <Field
          labelProps={{
            children: 'Código postal',
            className: 'text-subtitle1-bold text-neutral-900',
          }}
          inputProps={{
            ...getInputProps(fields.postalCode, { type: 'text' }),
            autoComplete: 'postal code',
            placeholder: 'Escribe aquí tu código postal',
            variant: 'outlined',
            className:
              ' max-w-[684px] !mt-3 !h-14 text-base-medium text-neutral-500 border-neutral-100',
            size: '64px',
          }}
          errors={fields.postalCode.errors}
        />

        <Field
          labelProps={{
            children: 'Email',
            className: 'text-subtitle1-bold text-neutral-900',
          }}
          inputProps={{
            ...getInputProps(fields.email, { type: 'text' }),
            autoComplete: 'email',
            placeholder: 'Escribe aquí tu email',
            variant: 'outlined',
            className:
              ' max-w-[684px] !mt-3 !h-14 text-base-medium text-neutral-500 border-neutral-100',
            size: '64px',
          }}
          errors={fields.email.errors}
        />

        <div className="max-w-[684px]">
          <PhoneField
            labelProps={{
              children: 'Teléfono',
              className: 'text-subtitle1-bold text-neutral-900',
            }}
            inputProps={{
              ...getInputProps(fields.phoneNumber, { type: 'tel' }),
              autoComplete: 'phone',
              placeholder: 'Ingresa tu teléfono',
              variant: 'outlined',
              className:
                '!mt-3 !h-14 text-base-medium text-neutral-500 border-neutral-100',
              size: '64px',
            }}
            fullPhone={fields.fullPhone.name}
            errors={fields.phoneNumber.errors}
          />
        </div>
      </div>
      <Spacer size="32px" sizeMd="64px" />

      <div
        className={`
          max-w-[684px]

          xl:mt-[3px]
        `}
      >
        <p className="text-h5-bold text-neutral-900">
          Detalles del pago para la reserva
        </p>

        <Spacer size="28px" sizeMd="32px" />
        <p className="text-subtitle1-bold text-neutral-900">
          Método del pago para la reserva
        </p>

        <Spacer size="12px" />

        <Tabs defaultValue={'stripe'}>
          <TabsList
            className={`
              flex !h-14 items-center gap-4 !rounded-[8px] !border
              !border-neutral-100 !bg-white px-1.5
            `}
          >
            <TabsTrigger
              className={`
                h-11 text-base-bold font-bold text-neutral-500

                data-[state=active]:rounded-base data-[state=active]:border
                data-[state=active]:border-neutral-100
                data-[state=active]:bg-neutral-25
                data-[state=active]:text-neutral-900

                hover:border-0 hover:text-neutral-500
              `}
              value={'stripe'}
              onClick={() => setPaymentMethod(PaymentEnum.STRIPE)}
              disabled={!isElementsReady || isLoading || !stripe || !elements}
            >
              <img
                src="/img/Frame 718.svg"
                className="mx-auto h-5 w-[72px]"
                alt="stripe-logo"
              />
            </TabsTrigger>
            <TabsTrigger
              className={`
                h-11 text-base-bold font-bold text-neutral-500

                data-[state=active]:rounded-base data-[state=active]:border
                data-[state=active]:border-neutral-100
                data-[state=active]:bg-neutral-25
                data-[state=active]:text-neutral-900

                hover:border-0 hover:text-neutral-500
              `}
              value={'paypal'}
              onClick={() => setPaymentMethod(PaymentEnum.PAYPAL)}
            >
              <img
                src="/img/paypal-3 1.svg"
                className="mx-auto h-4 w-[60.1px]"
                alt="paypal-logo"
              />
            </TabsTrigger>
          </TabsList>

          <TabsContent value={'stripe'}>
            {isElementsReady && (
              <>
                <PaymentElement id="payment-element" />
                {Boolean(message || error) && (
                  <ErrorList
                    id="payment-message"
                    errors={[
                      ...(message ? [message] : []),
                      ...(error ? [paymentErrorMessage(error)] : []),
                    ]}
                  />
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value={'paypal'}>
            <div
              className={`
                flex h-[200px] flex-col items-center justify-center gap-4
                rounded-base border border-neutral-75 bg-white

                xl:w-[684px]
              `}
            >
              <p className="max-w-[411px] text-center text-h4 text-neutral-900">
                Serás redirigido a PayPal para completar la reserva de manera
                segura
              </p>
              <img
                src="/img/image 64.png"
                className="h-12 w-[350px]"
                alt="paypal"
              />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Spacer size="24px" />
      <div className="flex flex-col gap-4">
        <div className="flex items-center">
          <Checkbox
            id="carAsPayment"
            checked={carAsPayment}
            onCheckedChange={() => setCarAsPayment(!carAsPayment)}
            className="z-10 border-neutral-100"
            color="gamaNeutral"
          />
          <label
            htmlFor="carAsPayment"
            className={`ml-2 text-base-medium text-neutral-900`}
          >
            Añadir coche como parte de pago
          </label>
        </div>

        <CheckboxTermsAndPrivacy
          isTermsAccepted={isTermsAccepted}
          setIsTermsAccepted={setIsTermsAccepted}
        />
      </div>
      <Spacer size="20px" />

      <p className="max-w-[684px] text-subtitle2-medium text-neutral-500">
        Pasados los 5 días del periodo de reserva sin que el cliente complete la
        formalización de la compraventa, se considerará que desiste de la
        adquisición del vehículo, y el importe pagado por el servicio de reserva
        no será reembolsado.
      </p>

      <Spacer size="28px" />

      <Button
        variant={'primary'}
        disabled={
          !isElementsReady ||
          isLoading ||
          !stripe ||
          !elements ||
          !isTermsAccepted
        }
        className={`
          px-6 py-5 text-base-bold text-white

          disabled:opacity-20
        `}
      >
        Reservarlo
      </Button>
    </Form>
  )
}
